import axios from "axios";
import {
  endSession,
  ensureFreshToken,
  readToken,
  refreshSession,
} from "./session";

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/hiring`;

// Which store Kumudu is currently working inside. Empty or absent means their
// own. Exported so the switcher and the interceptor cannot disagree about the
// key — a typo in one of them would look like the switcher silently not working.
export const ACTING_AS_KEY = "hiring.actingAsStoreId";

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Attaches the session token, renewing it first if it is about to expire.
apiClient.interceptors.request.use(
  async (config) => {
    // Renewed here rather than on a timer, because a timer only fires while
    // this tab is awake. A laptop closed at 6pm and opened at 9am has a token
    // that expired overnight and no interval that ever ran; renewing at the
    // moment of use is the only point that is guaranteed to happen.
    //
    // Costs nothing on the common path: a token with time left on it is a
    // localStorage read and a cached expiry check, no network call.
    const token = await ensureFreshToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // ── Kumudu working inside a customer store ─────────────────────────────
    //
    // Set by the store switcher. The server honours it ONLY when the token
    // belongs to the platform store and the user holds a platform role, so a
    // customer who sets this by hand simply gets their own data back — the
    // header names a tenant, it never authenticates one.
    //
    // Read from localStorage rather than passed through React state because
    // every service in this module would otherwise have to thread it, and the
    // one that forgot would silently read the wrong store.
    try {
      const actingAs = localStorage.getItem(ACTING_AS_KEY);
      if (actingAs) config.headers["X-Hiring-Store-Id"] = actingAs;
    } catch {
      // Private mode, blocked storage. Falling through means Kumudu sees its
      // own store, which is the safe direction.
    }

    // ── File uploads must not inherit the JSON default above ───────────────
    //
    // A multipart body is only parseable if its Content-Type carries the
    // boundary string that separates the parts, and only the browser knows what
    // boundary it generated. Leaving the instance default in place sends
    // `application/json` instead, so the server sees a body it cannot split:
    // multer finds no files, and the request fails as "No resumes were
    // uploaded" — which reads like the client sent nothing, when in fact it
    // sent everything and labelled it wrong.
    //
    // Deleting the header makes axios ask the browser, which fills in
    // `multipart/form-data; boundary=…`.
    //
    // Handled here rather than in each calling service because the trap is in
    // this file. Every future upload through apiClient would hit it, and the
    // symptom points at the server rather than at the header.
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ── The session ending is a 401, not a failed fetch ─────────────────────────
//
// Everything above keeps the token fresh while the session is alive. This is
// what happens when it is not — and it exists because the page it protects used
// to lie. Every screen in this module catches errors the same way:
//
//   catch (err) { setError("Failed to load jobs. Please try again."); }
//
// so an expired session arrived looking like a flaky server, and "try again"
// was advice that could not work. One retry after a renewal, and an honest
// sign-out when even that fails, is the difference.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Only a 401 is a session problem. A 403 means the session is fine and the
    // answer is still no — renewing that would just mint a token with exactly
    // the same claims and ask the same question twice.
    if (status !== 401 || !original || original._sessionRetry) {
      return Promise.reject(error);
    }

    // A 401 can still reach here with a perfectly fresh token: the request
    // interceptor renews on a five-minute margin, but a request queued behind a
    // slow upload can outlive that. Renew and replay once — the flag is what
    // keeps "once" honest, since the replay goes back through this interceptor.
    original._sessionRetry = true;

    const before = readToken();

    try {
      await refreshSession();

      // The replay goes back through the request interceptor above, which
      // reads the token that was just written and stamps the header itself.
      // Setting Authorization here instead would mean rewriting `headers`,
      // and by this point that is an AxiosHeaders instance — spreading it to
      // add "Authorization" leaves the normalised "authorization" from the
      // first attempt sitting beside the new one, and which of the two
      // survives the merge is not a thing to depend on.
      return apiClient(original);
    } catch {
      // Refusing to renew is the server saying the session is over: idle past
      // the refresh window, or the account deactivated. Nothing the UI can do
      // with that except say so.
      //
      // Guarded on the token being unchanged so a refresh that merely failed to
      // reach the network — the laptop lid was shut, the wifi dropped — while
      // some other tab quietly succeeded does not sign the person out anyway.
      if (readToken() === before) {
        await endSession();
      }
      return Promise.reject(error);
    }
  },
);
